// React hook wrapping fetchSubscriptionState. Used by widgets that need
// to derive their initial render variant from the dossier state of the
// subscription they're scoped to.
//
// Single responsibility: fetch ONCE on mount, return {loading, state, error}.
// Live updates via SSE are out of scope here — Phase 9 adds that.

import { useEffect, useState } from 'react';
import { fetchSubscriptionState, type SubscriptionState } from './investor';

export type SubscriptionStateHook = {
  loading: boolean;
  state: SubscriptionState | null;
  error: Error | null;
};

export function useSubscriptionState(
  subscriptionId: string | undefined,
  refetchKey: number = 0,
): SubscriptionStateHook {
  const [loading, setLoading] = useState<boolean>(Boolean(subscriptionId));
  const [state, setState] = useState<SubscriptionState | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!subscriptionId) {
      setLoading(false);
      setState(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await fetchSubscriptionState(subscriptionId);
        if (!cancelled) {
          setState(result);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e as Error);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subscriptionId, refetchKey]);

  return { loading, state, error };
}
